import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { adminApiService } from '../../services/adminApi';
import { tokenManager, securityUtils } from '../../services/api';

// Enhanced async thunks with Bearer token security
export const fetchAdmins = createAsyncThunk(
  'admins/fetchAdmins',
  async (_, { rejectWithValue }) => {
    try {
      // Validate authentication before API call
      if (!securityUtils.validateCurrentToken()) {
        throw new Error('Authentication required');
      }
      
      const response = await adminApiService.admins.getAll();
      return response.data || response; // Handle different response structures
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch admins');
    }
  }
);

export const createAdmin = createAsyncThunk(
  'admins/createAdmin',
  async (adminData, { rejectWithValue }) => {
    try {
      // Validate authentication before API call
      if (!securityUtils.validateCurrentToken()) {
        throw new Error('Authentication required');
      }
      
      const response = await adminApiService.admins.create(adminData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to create admin');
    }
  }
);

export const updateAdmin = createAsyncThunk(
  'admins/updateAdmin',
  async ({ id, adminData }, { rejectWithValue }) => {
    try {
      // Validate authentication before API call
      if (!securityUtils.validateCurrentToken()) {
        throw new Error('Authentication required');
      }
      
      const response = await adminApiService.admins.update(id, adminData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update admin');
    }
  }
);

export const deleteAdmin = createAsyncThunk(
  'admins/deleteAdmin',
  async (id, { rejectWithValue }) => {
    try {
      // Validate authentication before API call
      if (!securityUtils.validateCurrentToken()) {
        throw new Error('Authentication required');
      }
      
      await adminApiService.admins.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete admin');
    }
  }
);

export const updateAdminPermissions = createAsyncThunk(
  'admins/updatePermissions',
  async ({ id, permissions }, { rejectWithValue }) => {
    try {
      // Validate authentication before API call
      if (!securityUtils.validateCurrentToken()) {
        throw new Error('Authentication required');
      }
      
      const response = await adminApiService.admins.updatePermissions(id, permissions);
      return { id, permissions: response.permissions };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update permissions');
    }
  }
);

const adminSlice = createSlice({
  name: 'admins',
  initialState: {
    admins: [],
    loading: false,
    error: null,
    selectedAdmin: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedAdmin: (state, action) => {
      state.selectedAdmin = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Admins
      .addCase(fetchAdmins.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdmins.fulfilled, (state, action) => {
        state.loading = false;
        state.admins = action.payload;
      })
      .addCase(fetchAdmins.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      // Create Admin
      .addCase(createAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAdmin.fulfilled, (state, action) => {
        state.loading = false;
        state.admins.push(action.payload);
      })
      .addCase(createAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      // Update Admin
      .addCase(updateAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAdmin.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.admins.findIndex(admin => admin.id === action.payload.id);
        if (index !== -1) {
          state.admins[index] = action.payload;
        }
      })
      .addCase(updateAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      // Delete Admin
      .addCase(deleteAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAdmin.fulfilled, (state, action) => {
        state.loading = false;
        state.admins = state.admins.filter(admin => admin.id !== action.payload);
      })
      .addCase(deleteAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      // Update Admin Permissions
      .addCase(updateAdminPermissions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAdminPermissions.fulfilled, (state, action) => {
        state.loading = false;
        const { id, permissions } = action.payload;
        const index = state.admins.findIndex(admin => admin.id === id);
        if (index !== -1) {
          state.admins[index].permissions = permissions;
        }
      })
      .addCase(updateAdminPermissions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      });
  },
});

export const { clearError, setSelectedAdmin } = adminSlice.actions;
export default adminSlice.reducer;
